import type { Chart } from "chart.js";

import { clamp, getViewportTransform, mapPointToViewport } from "../selection-zoom/geometry";
import type { IViewportZoomBounds } from "../selection-zoom/types";

export const POLAR_ANGLE_INDEX_OFFSET = -89;

const ANGULAR_LABEL_STEP_DEGREES = 30;
const ANGULAR_LABEL_MIN_SEGMENT_LENGTH = 36;
const SENSOR_MARKER_OVERLAP_DEGREES = 5;
const DEGREE_SYMBOL = "\u00B0";

export type OrbitRadialScale = {
  xCenter: number;
  yCenter: number;
  drawingArea?: number;
  max?: number;
  getIndexAngle: (theta: number) => number;
  getDistanceFromCenterForValue: (r: number) => number;
};

export type IViewportAngularScaleLabelOptions =
  | boolean
  | {
      color?: string;
      backgroundColor?: string;
      fontFamily?: string;
      fontSize?: number;
      fontWeight?: string | number;
      padding?: number;
      borderColor?: string;
      leaderColor?: string;
    };

export interface IViewportAngularScaleLabelPluginOptions {
  viewport?: IViewportZoomBounds;
  viewportAngularScaleLabels?: IViewportAngularScaleLabelOptions;
}

type Rect = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

type SensorMarkerChartData = {
  _sensorMarkers?: Array<{ angle: number }>;
};

type Point = {
  x: number;
  y: number;
};

type LineSegment = {
  start: Point;
  end: Point;
};

type LabelBox = Rect & {
  textX: number;
  textY: number;
  score: number;
};

type LabelAnchor = LabelBox & {
  anchorX: number;
  anchorY: number;
};

type TextLabelAnchor = LabelAnchor & {
  text: string;
};

type AnchorEdge = "top" | "right" | "bottom" | "left" | "inside";

interface IAngularScaleLabelStyle {
  color: string;
  backgroundColor: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: string | number;
  padding: number;
  borderColor: string;
  leaderColor: string;
}

const isOrbitRadialScale = (scale: Partial<OrbitRadialScale> | undefined): scale is OrbitRadialScale =>
  !!scale &&
  Number.isFinite(scale.xCenter) &&
  Number.isFinite(scale.yCenter) &&
  typeof scale.getIndexAngle === "function" &&
  typeof scale.getDistanceFromCenterForValue === "function";

export const getOrbitRadialScale = (chart: any): OrbitRadialScale | null => {
  const scales = [chart.scales?.r, chart.getDatasetMeta?.(0)?.rScale] as Array<Partial<OrbitRadialScale> | undefined>;

  return scales.find(isOrbitRadialScale) ?? null;
};

const getAngularScaleLabelStyle = (option?: IViewportAngularScaleLabelOptions): IAngularScaleLabelStyle | null => {
  if (!option) {
    return null;
  }

  const overrides = typeof option === "object" ? option : {};

  return {
    color: overrides.color ?? "rgba(17, 24, 39, 0.96)",
    backgroundColor: overrides.backgroundColor ?? "rgba(255, 255, 255, 0.98)",
    fontFamily: overrides.fontFamily ?? "Roboto, sans-serif",
    fontSize: overrides.fontSize ?? 14,
    fontWeight: overrides.fontWeight ?? 700,
    padding: overrides.padding ?? 5,
    borderColor: overrides.borderColor ?? "rgba(17, 24, 39, 0.45)",
    leaderColor: overrides.leaderColor ?? "rgba(17, 24, 39, 0.45)",
  };
};

const getChartAreaRect = (chart: Chart): Rect => ({
  left: chart.chartArea?.left ?? 0,
  right: chart.chartArea?.right ?? chart.width,
  top: chart.chartArea?.top ?? 0,
  bottom: chart.chartArea?.bottom ?? chart.height,
});

const rectOverlaps = (a: Rect, b: Rect): boolean =>
  a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

const getBoxForAnchorPoint = (
  anchorX: number,
  anchorY: number,
  edge: AnchorEdge,
  textWidth: number,
  chartArea: Rect,
  style: IAngularScaleLabelStyle,
  baseScore: number,
): LabelAnchor | null => {
  const margin = 4;
  const boxWidth = textWidth + style.padding * 2;
  const boxHeight = style.fontSize + style.padding * 2;
  const sideOffset = 7;

  if (
    chartArea.right - chartArea.left < boxWidth + margin * 2 ||
    chartArea.bottom - chartArea.top < boxHeight + margin * 2
  ) {
    return null;
  }

  let rawLeft = anchorX - boxWidth / 2;
  let rawTop = anchorY - boxHeight / 2;

  if (edge === "top") {
    rawTop = anchorY + sideOffset;
  } else if (edge === "bottom") {
    rawTop = anchorY - boxHeight - sideOffset;
  } else if (edge === "left") {
    rawLeft = anchorX + sideOffset;
  } else if (edge === "right") {
    rawLeft = anchorX - boxWidth - sideOffset;
  } else if (anchorX <= (chartArea.left + chartArea.right) / 2) {
    rawLeft = anchorX + sideOffset;
  } else {
    rawLeft = anchorX - boxWidth - sideOffset;
  }

  const left = clamp(rawLeft, chartArea.left + margin, chartArea.right - boxWidth - margin);
  const top = clamp(rawTop, chartArea.top + margin, chartArea.bottom - boxHeight - margin);
  const detachPenalty = Math.abs(left - rawLeft) + Math.abs(top - rawTop);

  return {
    left,
    right: left + boxWidth,
    top,
    bottom: top + boxHeight,
    textX: left + style.padding,
    textY: top + boxHeight / 2,
    anchorX,
    anchorY,
    score: baseScore + detachPenalty * 30,
  };
};

const getPolarDegreeLinePoint = (rScale: OrbitRadialScale, degree: number, distanceFromCenter: number): Point => {
  const angleRad = rScale.getIndexAngle(degree + POLAR_ANGLE_INDEX_OFFSET);

  return {
    x: rScale.xCenter + Math.cos(angleRad) * distanceFromCenter,
    y: rScale.yCenter - Math.sin(angleRad) * distanceFromCenter,
  };
};

const clipLineSegmentToRect = (start: Point, end: Point, rect: Rect): LineSegment | null => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  let t0 = 0;
  let t1 = 1;

  const clip = (p: number, q: number): boolean => {
    if (p === 0) {
      return q >= 0;
    }

    const ratio = q / p;

    if (p < 0) {
      if (ratio > t1) {
        return false;
      }

      if (ratio > t0) {
        t0 = ratio;
      }
    } else {
      if (ratio < t0) {
        return false;
      }

      if (ratio < t1) {
        t1 = ratio;
      }
    }

    return true;
  };

  if (
    !clip(-dx, start.x - rect.left) ||
    !clip(dx, rect.right - start.x) ||
    !clip(-dy, start.y - rect.top) ||
    !clip(dy, rect.bottom - start.y)
  ) {
    return null;
  }

  return {
    start: {
      x: start.x + t0 * dx,
      y: start.y + t0 * dy,
    },
    end: {
      x: start.x + t1 * dx,
      y: start.y + t1 * dy,
    },
  };
};

const getPointDistanceSquared = (a: Point, b: Point): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;

  return dx * dx + dy * dy;
};

const getSegmentLength = (segment: LineSegment): number =>
  Math.hypot(segment.end.x - segment.start.x, segment.end.y - segment.start.y);

const getNearestChartAreaEdge = (point: Point, chartArea: Rect): AnchorEdge => {
  const edgeDistances: Array<{ edge: AnchorEdge; distance: number }> = [
    { edge: "top", distance: Math.abs(point.y - chartArea.top) },
    { edge: "right", distance: Math.abs(chartArea.right - point.x) },
    { edge: "bottom", distance: Math.abs(chartArea.bottom - point.y) },
    { edge: "left", distance: Math.abs(point.x - chartArea.left) },
  ];
  edgeDistances.sort((a, b) => a.distance - b.distance);

  return edgeDistances[0].distance <= 3 ? edgeDistances[0].edge : "inside";
};

const findAngularScaleLabelBox = (
  segment: LineSegment,
  center: Point,
  textWidth: number,
  chartArea: Rect,
  style: IAngularScaleLabelStyle,
  placedBoxes: Rect[],
): LabelAnchor | null => {
  const anchorCandidates = [segment.start, segment.end]
    .map(point => ({
      point,
      edge: getNearestChartAreaEdge(point, chartArea),
      distanceFromCenter: getPointDistanceSquared(point, center),
    }))
    .filter(candidate => candidate.edge !== "inside")
    .sort((a, b) => b.distanceFromCenter - a.distanceFromCenter);
  const candidates: LabelAnchor[] = [];

  anchorCandidates.forEach(({ point, edge }, index) => {
    const candidate = getBoxForAnchorPoint(point.x, point.y, edge, textWidth, chartArea, style, index * 20);

    if (candidate) {
      candidates.push(candidate);
    }
  });

  candidates.sort((a, b) => a.score - b.score);

  return candidates.find(candidate => placedBoxes.every(box => !rectOverlaps(candidate, box))) ?? null;
};

const drawLabelBox = (ctx: CanvasRenderingContext2D, box: TextLabelAnchor, style: IAngularScaleLabelStyle): void => {
  const leaderEndX = clamp(box.anchorX, box.left, box.right);
  const leaderEndY = clamp(box.anchorY, box.top, box.bottom);

  ctx.strokeStyle = style.leaderColor;
  ctx.lineWidth = 1.25;
  ctx.beginPath();
  ctx.moveTo(box.anchorX, box.anchorY);
  ctx.lineTo(leaderEndX, leaderEndY);
  ctx.stroke();

  ctx.fillStyle = style.backgroundColor;
  ctx.fillRect(box.left, box.top, box.right - box.left, box.bottom - box.top);
  ctx.strokeStyle = style.borderColor;
  ctx.lineWidth = 1.25;
  ctx.strokeRect(box.left, box.top, box.right - box.left, box.bottom - box.top);
  ctx.fillStyle = style.color;
  ctx.fillText(box.text, box.textX, box.textY);
};

const toTextLabelAnchor = (box: LabelAnchor, text: string): TextLabelAnchor => ({
  ...box,
  text,
});

export const drawViewportAngularScaleLabels = (
  chart: Chart,
  options?: IViewportAngularScaleLabelPluginOptions | false,
): void => {
  const style = getAngularScaleLabelStyle(options ? options.viewportAngularScaleLabels : undefined);

  if (!style || !options || !options.viewport) {
    return;
  }

  const rScale = getOrbitRadialScale(chart);
  if (!rScale) {
    return;
  }

  const transform = getViewportTransform(
    {
      left: 0,
      right: chart.width,
      top: 0,
      bottom: chart.height,
    },
    options.viewport,
  );

  if (!transform) {
    return;
  }

  const outerRadius =
    rScale.drawingArea ?? (Number.isFinite(rScale.max) ? rScale.getDistanceFromCenterForValue(Number(rScale.max)) : 0);

  if (!Number.isFinite(outerRadius) || outerRadius <= 0) {
    return;
  }

  const chartArea = getChartAreaRect(chart);
  const center = mapPointToViewport({ x: rScale.xCenter, y: rScale.yCenter }, transform);
  const placedBoxes: Rect[] = [];
  const sensorMarkers = (chart.data as SensorMarkerChartData)._sensorMarkers ?? [];
  const ctx = chart.ctx;

  ctx.save();
  ctx.font = `${style.fontWeight} ${style.fontSize}px ${style.fontFamily}`;
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";

  for (let degree = 0; degree < 360; degree += ANGULAR_LABEL_STEP_DEGREES) {
    const overlapsSensorMarker = sensorMarkers.some(({ angle }) => {
      const difference = Math.abs(((((degree - angle) % 360) + 540) % 360) - 180);
      return difference <= SENSOR_MARKER_OVERLAP_DEGREES;
    });

    if (overlapsSensorMarker) {
      continue;
    }

    const lineEnd = mapPointToViewport(getPolarDegreeLinePoint(rScale, degree, outerRadius), transform);
    const segment = clipLineSegmentToRect(center, lineEnd, chartArea);

    if (!segment || getSegmentLength(segment) < ANGULAR_LABEL_MIN_SEGMENT_LENGTH) {
      continue;
    }

    const label = `${degree}${DEGREE_SYMBOL}`;
    const textWidth = ctx.measureText(label).width;
    const box = findAngularScaleLabelBox(segment, center, textWidth, chartArea, style, placedBoxes);

    if (!box) {
      continue;
    }

    drawLabelBox(ctx, toTextLabelAnchor(box, label), style);
    placedBoxes.push(box);
  }

  ctx.restore();
};
